import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, FileDown, X, Check, Search, Receipt, User } from "lucide-react";
import { api, BACKEND_URL } from "../../lib/api";

// ── Constants ────────────────────────────────────────────────────────────────
const DISC = 0.15254;
const CGST = 0.09;
const SGST = 0.09;

const fmtINR = (n) =>
  "₹" + Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

function calcLine(qty, rate) {
  const gross   = qty * rate;
  const disc    = gross * DISC;
  const afterD  = gross - disc;
  const taxable = afterD / (1 + CGST + SGST);
  return {
    gross,
    disc,
    taxable,
    cgst: taxable * CGST,
    sgst: taxable * SGST,
  };
}

function calcTotals(items) {
  const lines = items.map((it) => calcLine(it.quantity, it.rate));
  const gross    = lines.reduce((s, l) => s + l.gross, 0);
  const taxable  = lines.reduce((s, l) => s + l.taxable, 0);
  const cgst     = lines.reduce((s, l) => s + l.cgst, 0);
  const sgst     = lines.reduce((s, l) => s + l.sgst, 0);
  const grand = Math.round(gross);
  return { gross, taxable, cgst, sgst, roundOff: grand - gross, grand, lines };
}

// ── Main component ───────────────────────────────────────────────────────────
export default function AdminBilling() {
  const [items,      setItems]      = useState([]);
  const [custName,   setCustName]   = useState("");
  const [custPhone,  setCustPhone]  = useState("");
  const [invoiceNo,  setInvoiceNo]  = useState("");
  const [generating, setGenerating] = useState(false);
  const [editIdx,    setEditIdx]    = useState(null);   // index being edited inline

  // Add-item form state
  const [desc, setDesc] = useState("");
  const [qty,  setQty]  = useState("1");
  const [rate, setRate] = useState("");

  // Autocomplete — all products loaded once, filtered client-side
  // allProducts: array of { name, price }
  const [allProducts, setAllProducts] = useState([]);
  const [acOpen,      setAcOpen]      = useState(false);
  const [acIdx,       setAcIdx]       = useState(0);
  const descRef = useRef(null);
  const acRef   = useRef(null);

  const totals = calcTotals(items);

  // Filtered list: show all when empty, filter by substring when typing
  const filtered = desc.trim()
    ? allProducts.filter((p) => p.name.toLowerCase().includes(desc.toLowerCase()))
    : allProducts;

  // ── Load products from the live website catalogue ───────────────────────
  useEffect(() => {
    api.get("/admin/products/all")
      .then((r) => {
        // Deduplicate by name, keep highest price for duplicates, sort by name
        const map = new Map();
        (r.data || []).forEach((p) => {
          if (!p.name) return;
          const existing = map.get(p.name);
          if (!existing || (p.price || 0) > existing.price) {
            map.set(p.name, { name: p.name, price: p.price || 0 });
          }
        });
        setAllProducts([...map.values()].sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => {});
  }, []);

  const onDescChange = (val) => {
    setDesc(val);
    setAcIdx(0);
    setAcOpen(true);
  };

  // p can be a { name, price } object or a plain string (custom item)
  const pickProduct = (p) => {
    const name  = typeof p === "string" ? p : p.name;
    const price = typeof p === "string" ? null : p.price;
    setDesc(name);
    if (price) setRate(String(price));
    setAcOpen(false);
    // Focus qty if rate filled, otherwise rate
    setTimeout(() => {
      document.getElementById(price ? "billing-qty" : "billing-rate")?.focus();
    }, 0);
  };

  // Close autocomplete on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!acRef.current?.contains(e.target)) setAcOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Add item ─────────────────────────────────────────────────────────────
  const addItem = () => {
    if (!desc.trim()) { toast.error("Enter a product name"); return; }
    const q = parseFloat(qty);
    const r = parseFloat(rate);
    if (!q || q <= 0) { toast.error("Enter a valid quantity"); return; }
    if (!r || r <= 0) { toast.error("Enter a valid rate"); return; }
    setItems((prev) => [...prev, { description: desc.trim(), quantity: q, rate: r }]);
    setDesc(""); setQty("1"); setRate(""); setAcOpen(false);
    descRef.current?.focus();
    toast.success("Item added");
  };

  // ── Remove / inline edit ──────────────────────────────────────────────────
  const removeItem = (i) => {
    setItems((prev) => prev.filter((_, idx) => idx !== i));
    if (editIdx === i) setEditIdx(null);
  };

  const saveEdit = (i, field, val) => {
    setItems((prev) =>
      prev.map((it, idx) =>
        idx === i
          ? { ...it, [field]: field === "quantity" || field === "rate" ? parseFloat(val) || it[field] : val }
          : it
      )
    );
  };

  // ── Generate PDF ──────────────────────────────────────────────────────────
  const generate = async () => {
    if (!custName.trim()) { toast.error("Enter customer name"); return; }
    if (!custPhone.trim()) { toast.error("Enter phone number"); return; }
    if (!items.length) { toast.error("Add at least one item"); return; }
    setGenerating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/billing/generate-invoice`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_name: custName,
          customer_phone: custPhone,
          invoice_no: invoiceNo,
          items: items.map((it) => ({
            description: it.description,
            quantity: it.quantity,
            rate: it.rate,
          })),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `Server error ${res.status}` }));
        throw new Error(err.error);
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `${custPhone.replace(/\D/g, "")}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Invoice downloaded!");
    } catch (e) {
      toast.error(e.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const clearAll = () => {
    setItems([]); setCustName(""); setCustPhone(""); setInvoiceNo("");
    setDesc(""); setQty("1"); setRate(""); setAcOpen(false); setEditIdx(null);
  };

  // ── Line preview ──────────────────────────────────────────────────────────
  const previewLine = (() => {
    const q = parseFloat(qty); const r = parseFloat(rate);
    if (!q || !r || q <= 0 || r <= 0) return null;
    return calcLine(q, r);
  })();

  return (
    <div>
      {/* Page header */}
      <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Operations</div>
      <h1 className="text-3xl font-black uppercase tracking-tighter mb-8">Invoice Generator</h1>

      <div className="grid lg:grid-cols-[420px_1fr] gap-6 items-start">

        {/* ── LEFT PANEL ─────────────────────────────────────────────────── */}
        <div className="space-y-4">

          {/* Customer */}
          <section className="bg-navy-800 rounded-xl border border-gold/20 p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold mb-4 flex items-center gap-2">
              <User className="w-3 h-3" /> Customer Details
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] text-slate-400 uppercase tracking-wider block mb-1">Name</label>
                <input
                  value={custName} onChange={(e) => setCustName(e.target.value)}
                  placeholder="e.g. Rahul Sharma"
                  className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-gold/50"
                />
              </div>
              <div>
                <label className="text-[11px] text-slate-400 uppercase tracking-wider block mb-1">Phone</label>
                <input
                  value={custPhone} onChange={(e) => setCustPhone(e.target.value)}
                  placeholder="e.g. 9876543210" maxLength={15} inputMode="tel"
                  className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-gold/50"
                />
              </div>
              <div>
                <label className="text-[11px] text-slate-400 uppercase tracking-wider block mb-1">
                  Invoice No <span className="text-slate-600 normal-case">(auto if blank)</span>
                </label>
                <input
                  value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)}
                  placeholder="GT/26-27/0001"
                  className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-gold/50"
                />
              </div>
            </div>
          </section>

          {/* Add Item */}
          <section className="bg-navy-800 rounded-xl border border-gold/20 p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold mb-4 flex items-center gap-2">
              <Plus className="w-3 h-3" /> Add Item
            </div>
            <div className="space-y-3">
              {/* Autocomplete */}
              <div>
                <label className="text-[11px] text-slate-400 uppercase tracking-wider block mb-1">Product</label>
                <div className="relative" ref={acRef}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                    <input
                      ref={descRef}
                      value={desc}
                      onChange={(e) => onDescChange(e.target.value)}
                      onFocus={() => setAcOpen(true)}
                      onKeyDown={(e) => {
                        if (!acOpen) { setAcOpen(true); return; }
                        if (e.key === "ArrowDown") { e.preventDefault(); setAcIdx((i) => Math.min(i + 1, filtered.length - 1)); }
                        else if (e.key === "ArrowUp") { e.preventDefault(); setAcIdx((i) => Math.max(i - 1, 0)); }
                        else if (e.key === "Enter") { e.preventDefault(); if (filtered[acIdx]) pickProduct(filtered[acIdx]); else if (desc.trim()) pickProduct(desc.trim()); }
                        else if (e.key === "Escape") setAcOpen(false);
                      }}
                      placeholder="Click to browse or type to search…"
                      autoComplete="off"
                      className="w-full bg-navy-900 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-gold/50"
                    />
                  </div>
                  {acOpen && (filtered.length > 0 || desc.trim()) && (
                    <div className="absolute top-full mt-1 left-0 right-0 bg-navy-900 border border-gold/30 rounded-lg z-50 max-h-56 overflow-y-auto shadow-2xl">
                      {filtered.map((p, i) => {
                        const q = desc.trim().toLowerCase();
                        const matchIdx = q ? p.name.toLowerCase().indexOf(q) : -1;
                        const highlighted = matchIdx >= 0
                          ? <>{p.name.slice(0, matchIdx)}<span className="text-gold font-bold">{p.name.slice(matchIdx, matchIdx + desc.length)}</span>{p.name.slice(matchIdx + desc.length)}</>
                          : p.name;
                        return (
                          <button
                            key={p.name}
                            onMouseDown={() => pickProduct(p)}
                            className={`w-full text-left px-3 py-2 text-sm border-b border-white/5 last:border-0 transition flex items-center justify-between gap-2 ${
                              i === acIdx ? "bg-gold/10 text-gold" : "text-slate-300 hover:bg-white/5"
                            }`}
                          >
                            <span>{highlighted}</span>
                            {p.price > 0 && <span className="text-[11px] text-slate-500 flex-shrink-0">{fmtINR(p.price)}</span>}
                          </button>
                        );
                      })}
                      {/* Allow custom name if typed text doesn't match any product exactly */}
                      {desc.trim() && !filtered.some((p) => p.name.toLowerCase() === desc.trim().toLowerCase()) && (
                        <button
                          onMouseDown={() => pickProduct(desc.trim())}
                          className="w-full text-left px-3 py-2 text-sm text-slate-400 hover:bg-white/5 border-t border-white/10 flex items-center gap-2"
                        >
                          <Plus className="w-3 h-3 text-gold flex-shrink-0" />
                          Add as <span className="text-white font-semibold truncate ml-1">"{desc.trim()}"</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] text-slate-400 uppercase tracking-wider block mb-1">Quantity</label>
                  <input
                    id="billing-qty"
                    type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)}
                    className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gold/50"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-slate-400 uppercase tracking-wider block mb-1">Rate (GST incl.)</label>
                  <input
                    id="billing-rate"
                    type="number" min="0" step="0.01" value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addItem()}
                    placeholder="₹"
                    className="w-full bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-gold/50"
                  />
                </div>
              </div>

              {/* Mini preview */}
              {previewLine && (
                <div className="bg-navy-900/60 rounded-lg p-3 text-[11px] border border-white/5 flex justify-between items-center">
                  <span className="text-slate-500">Total</span>
                  <span className="font-bold text-white">{fmtINR(previewLine.gross)}</span>
                </div>
              )}

              <button
                onClick={addItem}
                className="w-full bg-gold text-navy-900 font-black text-xs uppercase tracking-wider py-2.5 rounded-lg hover:bg-gold/90 transition flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" /> Add to Invoice
              </button>
            </div>
          </section>

          {/* Totals */}
          {items.length > 0 && (
            <section className="bg-navy-800 rounded-xl border border-gold/20 p-5">
              <div className="flex justify-between items-center">
                <span className="font-black text-base uppercase tracking-wider">Grand Total</span>
                <span className="font-black text-xl text-gold">{fmtInt(totals.grand)}</span>
              </div>
            </section>
          )}
        </div>

        {/* ── RIGHT PANEL — items table + actions ──────────────────────── */}
        <div className="space-y-4">
          <section className="bg-navy-800 rounded-xl border border-gold/20 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold flex items-center gap-2">
                <Receipt className="w-3 h-3" /> Invoice Items
              </div>
              <span className="text-xs font-bold bg-gold/10 text-gold px-2 py-0.5 rounded-full">
                {items.length} item{items.length !== 1 ? "s" : ""}
              </span>
            </div>

            {items.length === 0 ? (
              <div className="py-16 text-center text-slate-600">
                <Receipt className="w-8 h-8 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No items added yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 border-b border-white/5">
                      <th className="px-4 py-3 text-left w-6">#</th>
                      <th className="px-4 py-3 text-left">Description</th>
                      <th className="px-4 py-3 text-right">Qty</th>
                      <th className="px-4 py-3 text-right">Rate</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                      <th className="px-4 py-3 text-center w-20">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => {
                      const line = totals.lines[i];
                      const isEditing = editIdx === i;
                      return (
                        <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition">
                          <td className="px-4 py-3 text-slate-500 font-mono">{i + 1}</td>
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <input
                                defaultValue={it.description}
                                onBlur={(e) => saveEdit(i, "description", e.target.value)}
                                className="w-full bg-navy-900 border border-gold/30 rounded px-2 py-1 text-xs text-white focus:outline-none"
                                autoFocus
                              />
                            ) : (
                              <div className="font-semibold text-white">{it.description}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isEditing ? (
                              <input
                                type="number" min="1" defaultValue={it.quantity}
                                onBlur={(e) => saveEdit(i, "quantity", e.target.value)}
                                className="w-16 bg-navy-900 border border-gold/30 rounded px-2 py-1 text-xs text-white text-right focus:outline-none"
                              />
                            ) : <span className="text-slate-300">{it.quantity}</span>}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isEditing ? (
                              <input
                                type="number" min="0" defaultValue={it.rate}
                                onBlur={(e) => saveEdit(i, "rate", e.target.value)}
                                className="w-24 bg-navy-900 border border-gold/30 rounded px-2 py-1 text-xs text-white text-right focus:outline-none"
                              />
                            ) : <span className="text-slate-300">{fmtINR(it.rate)}</span>}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-white">{fmtINR(line.gross)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              {isEditing ? (
                                <button
                                  onClick={() => setEditIdx(null)}
                                  className="p-1.5 rounded-md bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition"
                                  title="Done"
                                >
                                  <Check className="w-3.5 h-3.5" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => setEditIdx(i)}
                                  className="p-1.5 rounded-md bg-white/5 text-slate-400 hover:text-gold hover:bg-gold/10 transition"
                                  title="Edit"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button
                                onClick={() => removeItem(i)}
                                className="p-1.5 rounded-md bg-white/5 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition"
                                title="Remove"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Action buttons */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={generate}
              disabled={generating || !items.length}
              className="flex items-center gap-2 bg-gold text-navy-900 font-black text-xs uppercase tracking-wider px-5 py-3 rounded-lg hover:bg-gold/90 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FileDown className="w-4 h-4" />
              {generating ? "Generating…" : "Generate Invoice PDF"}
            </button>
            {items.length > 0 && (
              <button
                onClick={clearAll}
                className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400 hover:text-rose-400 border border-white/10 hover:border-rose-500/30 px-4 py-3 rounded-lg transition"
              >
                <X className="w-3.5 h-3.5" /> Clear All
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Small helper ─────────────────────────────────────────────────────────────
function Row({ label, value, cls = "text-slate-300" }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-500">{label}</span>
      <span className={cls}>{value}</span>
    </div>
  );
}
